import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, UserCircle } from 'lucide-react';
import { logger } from '../services/logger.ts';
import { AdminUserOverview, fetchAdminUserOverview, fetchAdminUserPortfolio, fetchAdminUserAffiliates, AffiliateUser } from '../services/adminUserService.ts';
import { PortfolioDataResult } from '../services/portfolioDataService.ts';
import { AdminUserSummaryCards } from '../components/admin/user-profile/AdminUserSummaryCards.tsx';
import { AdminUserPositionsTable } from '../components/admin/user-profile/AdminUserPositionsTable.tsx';
import { AdminUserTransactionsTable } from '../components/admin/user-profile/AdminUserTransactionsTable.tsx';
import { AdminUserContactCard } from '../components/admin/user-profile/AdminUserContactCard.tsx';
import { AdminUserCreatedMarketsSection } from '../components/admin/user-profile/AdminUserCreatedMarketsSection.tsx';
import { AdminUserAffiliatesSection } from '../components/admin/user-profile/AdminUserAffiliatesSection.tsx';

type AdminProfileTab = 'overview' | 'positions' | 'transactions' | 'created' | 'contact' | 'affiliates';

export function AdminUserProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminProfileTab>('overview');
  const [overview, setOverview] = useState<AdminUserOverview | null>(null);
  const [portfolioData, setPortfolioData] = useState<PortfolioDataResult | null>(null);
  const [affiliates, setAffiliates] = useState<AffiliateUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset to overview tab when navigating to a different user
  useEffect(() => {
    setActiveTab('overview');
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [overviewResult, portfolioResult, affiliatesResult] = await Promise.all([
          fetchAdminUserOverview(id),
          fetchAdminUserPortfolio(id),
          fetchAdminUserAffiliates(id),
        ]);

        if (!isMounted) {
          return;
        }

        if (!overviewResult) {
          setError('User not found.');
          return;
        }

        setOverview(overviewResult);
        setPortfolioData(portfolioResult);
        setAffiliates(affiliatesResult);
      } catch (err) {
        logger.error('Failed to load admin user profile:', err);
        if (isMounted) {
          setError('Unable to load user profile. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const computedStats = useMemo(() => {
    if (!portfolioData) {
      return {
        portfolioValue: 0,
        openPositions: 0,
        winRate: 0,
        totalTrades: 0,
      };
    }

    const openPositions = portfolioData.positions.filter((position) => position.status === 'active').length;
    const portfolioValue = portfolioData.positions
      .filter((position) => position.status === 'active')
      .reduce((sum, position) => sum + (position.currentPotentialWin ?? 0), 0);

    return {
      portfolioValue,
      openPositions,
      winRate: portfolioData.winRate,
      totalTrades: portfolioData.totalTrades,
    };
  }, [portfolioData]);

  const pageTitle = overview?.fullName || overview?.email || 'User Profile';

  if (!id) {
    return null;
  }

  if (loading) {
    return (
      <div className="py-12">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-wayja-teal" />
        </div>
      </div>
    );
  }

  if (error || !overview || !portfolioData) {
    return (
      <div className="py-12">
        <div className="max-w-3xl mx-auto bg-white border border-red-200 rounded-lg p-8 text-center">
          <p className="text-red-600 font-medium mb-4">{error || 'Unable to load user data.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => navigate(-1)} 
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-wayja-teal/10 flex items-center justify-center">
            <UserCircle className="w-7 h-7 text-wayja-teal" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{pageTitle}</h1>
            <p className="text-sm text-gray-500">{overview.email}</p>
          </div>
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200 overflow-x-auto">
        <nav className="flex -mb-px min-w-full">
          {TAB_OPTIONS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`py-3 px-5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.value
                  ? 'border-wayja-teal text-wayja-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-6">
        {activeTab === 'overview' && (
          <>
            <AdminUserSummaryCards
              overview={overview}
              portfolioValue={computedStats.portfolioValue}
              winRate={computedStats.winRate}
              openPositions={computedStats.openPositions}
              totalTrades={computedStats.totalTrades}
            />
            <AdminUserContactCard overview={overview} />
          </>
        )}

        {activeTab === 'positions' && (
          <AdminUserPositionsTable positions={portfolioData.positions} />
        )}

        {activeTab === 'transactions' && (
          <AdminUserTransactionsTable transactions={portfolioData.transactions} />
        )}

        {activeTab === 'created' && (
          <AdminUserCreatedMarketsSection markets={portfolioData.createdMarkets} />
        )}

        {activeTab === 'contact' && (
          <AdminUserContactCard overview={overview} />
        )}

        {activeTab === 'affiliates' && (
          <AdminUserAffiliatesSection affiliates={affiliates} />
        )}
      </div>
    </div>
  );
}

const TAB_OPTIONS: Array<{ value: AdminProfileTab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'positions', label: 'Positions' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'created', label: 'Created Markets' },
  { value: 'affiliates', label: 'Affiliates' },
  { value: 'contact', label: 'Contact' },
];

